import { useMemo, useState, useRef } from 'react';

// material-ui
import {
  FormControl,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  OutlinedInputProps,
  Select,
  Slider,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SearchOutlined, CloseOutlined, Refresh } from '@mui/icons-material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

// third-party
import { useAsyncDebounce, Row, TableState, MetaBase, HeaderProps } from 'react-table';
import { matchSorter } from 'match-sorter';
import { format } from 'date-fns';

// project-imports
import IconButton from 'components/@extended/IconButton';

// assets
import { Add, Minus } from 'iconsax-react';
import { Menu } from '@mui/material';
import { Typography } from 'components/elements';
import { cloneDeep } from 'lodash';
import { ReviewDocumentStatusType } from 'types/prediction';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { openDefaultSuccessSnackbar } from 'store/reducers/snackbar';

// ==============================|| CUSTOM FUNCTION - REACT TABLE ||============================== //

interface GlobalFilterProps extends OutlinedInputProps {
  preGlobalFilteredRows: Row<{}>[];
  globalFilter: string;
  setGlobalFilter: (value: string | undefined) => void;
  unSelectAllRows: () => void;
}

export function GlobalFilter({ preGlobalFilteredRows, globalFilter, setGlobalFilter, unSelectAllRows, ...other }: GlobalFilterProps) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = useState(globalFilter);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  const handleClear = () => {
    setValue('');
    onChange('');
    setIsFocused(false);
    unSelectAllRows();
  };

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onChangeIntercept = (newValue: any) => {
    setValue(newValue);
    onChange(newValue);
    unSelectAllRows();
  };

  return (
    <OutlinedInput
      inputRef={inputRef}
      value={value || ''}
      onChange={(e) => onChangeIntercept(e.target.value)}
      placeholder={other.placeholder ?? `Search ${count} records...`}
      id="start-table-search"
      className={`GlobalSearchFilter ${isFocused || value ? 'focused' : ''}`}
      startAdornment={
        <InputAdornment position="start">
          <IconButton onClick={handleFocus} edge="start" size="medium">
            <SearchOutlined color="action" style={{ fontSize: '1.25rem' }} />
          </IconButton>
        </InputAdornment>
      }
      endAdornment={
        value && (
          <InputAdornment position="end">
            <IconButton onClick={handleClear} edge="end" size="small">
              <CloseOutlined fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }
      {...other}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        if (!value) setValue(globalFilter);
      }}
      sx={{
        width: other.fullWidth ? '100%' : isFocused || value ? 300 : 40,
        transition: 'width 0.3s',
        '& .MuiOutlinedInput-input': {
          width: '100%',
          '&:focus': {
            width: 'calc(100% - 24px)'
          }
        },
        '& .MuiInputAdornment-positionStart': {
          marginRight: '8px'
        }
      }}
    />
  );
}

export function DefaultColumnFilter({ column: { filterValue, Header, setFilter } }: any) {
  return (
    <TextField
      fullWidth
      value={filterValue || ''}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={Header}
      size="small"
    />
  );
}

export function DateColumnFilter({ column: { filterValue, Header, setFilter } }: any) {
  return (
    <FormControl sx={{ width: '100%' }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          format="dd/MM/yyyy"
          value={filterValue && new Date(filterValue)}
          onChange={(newValue) => {
            let formatDateFn = undefined;
            try {
              formatDateFn = format(newValue, 'M/d/yyyy');
            } catch (error) {}
            setFilter(formatDateFn || undefined);
          }}
          slotProps={{ inputAdornment: {} }}
        />
      </LocalizationProvider>
    </FormControl>
  );
}

interface FilterColumnProps {
  filterValue?: never[];
  preFilteredRows: Row[];
  Header: string;
  setFilter: any;
  id: string;
}

export function SelectColumnFilter({ column: { filterValue, setFilter, preFilteredRows, id } }: { column: FilterColumnProps }) {
  const options = useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row: Row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <Select
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      displayEmpty
      size="small"
    >
      <MenuItem value="">All</MenuItem>
      {options.map((option: any, i: number) => (
        <MenuItem key={i} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
}

export function SliderColumnFilter({ column: { filterValue, setFilter, preFilteredRows, id } }: { column: FilterColumnProps }) {
  const [min, max] = useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach((row: Row) => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1, minWidth: 120 }}>
      <Slider
        value={filterValue || min}
        min={min}
        max={max}
        step={1}
        onChange={(event: Event, newValue: number | number[]) => {
          setFilter(newValue);
        }}
        valueLabelDisplay="auto"
        aria-labelledby="non-linear-slider"
      />
      <Tooltip title="Reset">
        <IconButton size="small" color="error" onClick={() => setFilter(undefined)}>
          <Add style={{ transform: 'rotate(45deg)' }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export function NumberRangeColumnFilter({ column: { filterValue = [], preFilteredRows, setFilter, id } }: { column: FilterColumnProps }) {
  const [min, max] = useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach((row: Row) => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 168, maxWidth: 250 }}>
      <TextField
        fullWidth
        value={filterValue[0] || ''}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [val ? parseInt(val, 10) : undefined, old[1]]);
        }}
        placeholder={`Min (${min})`}
        size="small"
      />
      <Minus />
      <TextField
        fullWidth
        value={filterValue[1] || ''}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [old[0], val ? parseInt(val, 10) : undefined]);
        }}
        placeholder={`Max (${max})`}
        size="small"
      />
    </Stack>
  );
}

function fuzzyTextFilterFn(rows: Row[], id: number, filterValue: string) {
  return matchSorter(rows, filterValue, { keys: [(row: Row) => row.values[id]] });
}

fuzzyTextFilterFn.autoRemove = (val: number) => !val;

export const renderFilterTypes: any = () => ({
  fuzzyText: fuzzyTextFilterFn,
  text: (rows: Row[], id: string, filterValue: string) => {
    rows.filter((row: Row) => {
      const rowValue = row.values[id];
      return rowValue !== undefined ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase()) : true;
    });
  }
});

export function filterGreaterThan(rows: Row[], id: number, filterValue: number) {
  return rows.filter((row: Row) => {
    const rowValue = row.values[id];
    return rowValue >= filterValue;
  });
}

filterGreaterThan.autoRemove = (val: number) => typeof val !== 'number';

export function useControlledState(state: TableState<{}>, { instance }: MetaBase<{}>) {
  return useMemo(() => {
    if (state.groupBy.length) {
      return {
        ...state,
        hiddenColumns: [...state.hiddenColumns!, ...state.groupBy].filter((d, i, all) => all.indexOf(d) === i)
      };
    }
    return state;
  }, [state]);
}

export function roundedMedian(leafValues: any) {
  let min = leafValues[0] || 0;
  let max = leafValues[0] || 0;

  leafValues.forEach((value: number) => {
    min = Math.min(min, value);
    max = Math.max(max, value);
  });

  return Math.round((min + max) / 2);
}

export const globalFilterForSubRows = (rows: Row[], columnIds: string[], searchQuery: string): Row[] => {
  if (!searchQuery) return rows;

  const lowercaseQuery = searchQuery.toLowerCase();
  const rowMatches = (row: Row): boolean =>
    Object.values(row.values).some((rowValue) => typeof rowValue === 'string' && rowValue.toLowerCase().includes(lowercaseQuery)) ||
    row.subRows.some(rowMatches);

  return rows.filter(rowMatches);
};

export const columnFilterForSubRows = (tableRows: Row[], columnIds: string[], searchQuery: string): Row[] => {
  const rows = cloneDeep(tableRows);
  if (!searchQuery) return rows;

  const lowercaseQuery = searchQuery.toLowerCase();

  const rowMatches = (row: Row): boolean =>
    [row.values[columnIds[0]]].some((rowValue) => {
      if (typeof rowValue === 'string') {
        return rowValue.toLowerCase().includes(lowercaseQuery);
      } else if (typeof rowValue === 'number') {
        return rowValue.toString().includes(lowercaseQuery);
      }
      return false;
    }) || row.subRows.some(rowMatches);

  return rows.filter(rowMatches);
};

export const TableRefereshButton = ({ onRefreshClick }: { onRefreshClick: () => void }) => {
  const dispatch = useDispatch();
  return (
    <IconButton
      onClick={() => {
        onRefreshClick();
        dispatch(
          openDefaultSuccessSnackbar({
            message: 'Refreshed'
          })
        );
      }}
      edge="start"
      size="medium"
      color="default"
      variant="contained"
    >
      <Refresh style={{ fontSize: '1.25rem' }} />
    </IconButton>
  );
};

export const ColumnLevelFilterButton = ({
  showColumnLevelFilterInputBox,
  setShowColumnLevelFilterInputBox
}: {
  showColumnLevelFilterInputBox: boolean;
  setShowColumnLevelFilterInputBox: any;
}) => {
  const onToggleFilter = () => {
    setShowColumnLevelFilterInputBox((preVal: boolean) => !preVal);
  };

  return (
    <IconButton
      onClick={onToggleFilter}
      edge="start"
      size="medium"
      color={'default'}
      variant={showColumnLevelFilterInputBox ? 'contained' : 'outlined'}
    >
      <SearchOutlined style={{ fontSize: '1.25rem' }} />
    </IconButton>
  );
};

export const TableFilterByColumn = ({
  setFilter,
  uniqueValuesByColumns = []
}: {
  setFilter: (columnId: string, updater: any) => void;
  uniqueValuesByColumns: { columnId: string; value: string }[];
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilter, setSelectedFilter] = useState<false | string>(false);

  const onFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilter = (columnId: string, value: string) => {
    handleMenuClose();
    resetColumnFilters();
    setFilter(columnId, value);
    setSelectedFilter(value);
  };

  const resetColumnFilters = () => {
    handleMenuClose();
    uniqueValuesByColumns.forEach(({ columnId }) => {
      setFilter(columnId, null);
    });
    setSelectedFilter(false);
  };

  const documentReadyButtonLangKeys = {
    [ReviewDocumentStatusType.READY]: 'elements.buttons.ready',
    [ReviewDocumentStatusType.UPLOADED]: 'pages.review.prediction.labels.uploaded',
    [ReviewDocumentStatusType.PROCESSING]: 'pages.review.prediction.labels.processing',
    [ReviewDocumentStatusType.QAINPROGRESS]: 'pages.review.prediction.labels.qaInProgress',
    [ReviewDocumentStatusType.INTERRUPTED]: 'pages.review.prediction.labels.interrupted',
    [ReviewDocumentStatusType.INPROGRESS]: 'pages.review.prediction.labels.inProgress',
    [ReviewDocumentStatusType.COMPLETED]: 'pages.review.prediction.labels.completed',
    [ReviewDocumentStatusType.VERIFIED]: 'pages.review.prediction.labels.verified',
    [ReviewDocumentStatusType.EXPORTED]: 'pages.review.prediction.labels.exported',
    [ReviewDocumentStatusType.CURATIONINPROGRESS]: 'pages.review.prediction.labels.curationInProgress',
    [ReviewDocumentStatusType.CURATIONINCOMPLETE]: 'pages.review.prediction.labels.curationInComplete',
    [ReviewDocumentStatusType['API-INTERRUPTED']]: 'pages.review.prediction.labels.apiInterrupted',
    [ReviewDocumentStatusType.AUTOQC]: 'pages.review.prediction.labels.autoQC',
    [ReviewDocumentStatusType.YETTOSTART]: 'pages.review.prediction.labels.yetToStart',
    [ReviewDocumentStatusType.READYFORQC]: 'elements.buttons.readyForQc',
    [ReviewDocumentStatusType.QCINPROGRESS]: 'pages.review.prediction.labels.qcInProgress',
    [ReviewDocumentStatusType.QCINCOMPLETE]: 'pages.review.prediction.labels.qcIncomplete',
    [ReviewDocumentStatusType.QCVERIFIED]: 'pages.review.prediction.labels.qcVerified',
    [ReviewDocumentStatusType.EXCEPTION]: 'pages.review.prediction.labels.exception',
    [ReviewDocumentStatusType.INITIATED]: 'pages.review.prediction.labels.initiated',
    [ReviewDocumentStatusType.INQUEUE]: 'pages.review.prediction.labels.inQueue',
    [ReviewDocumentStatusType.VOLUMEEXCEEDED]: 'pages.review.prediction.labels.volumeExceeded',
    [ReviewDocumentStatusType.LOWDISKSPACE]: 'pages.review.prediction.labels.lowDiskSpace',
    [ReviewDocumentStatusType.READYFORCURATION]: 'pages.review.verification.status.readyForCuration',
    [ReviewDocumentStatusType.CURATIONCOMPLETED]: 'pages.review.verification.status.curationCompleted'
  };

  return (
    <>
      <IconButton onClick={onFilterClick} edge="start" size="medium" color="default" variant="contained">
        <FilterAltOutlinedIcon style={{ fontSize: '1.25rem' }} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} className={'table-column-filter-menu'}>
        <MenuItem key={'All'} onClick={resetColumnFilters} className={selectedFilter === false ? 'active' : ''}>
          <Typography>All</Typography>
        </MenuItem>
        {uniqueValuesByColumns.map(({ columnId, value }, key) => {
          const langKey = documentReadyButtonLangKeys[value as keyof typeof documentReadyButtonLangKeys];

          return (
            <MenuItem key={key} onClick={() => handleFilter(columnId, value)} className={selectedFilter === value ? 'active' : ''}>
              {langKey ? (
                <Typography style={{ textTransform: 'none' }}>{langKey ? <FormattedMessage id={langKey} /> : value}</Typography>
              ) : (
                value
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export const generateIndexedArrayWithSubRows = (data: any[], expandedRows: any = {}) => {
  let indexedArray: any[] = [];
  const generateRows = (row: any[], parentKey?: any) => {
    row.forEach(({ subRows }, key) => {
      let indexedKey = (parentKey >= 0 ? `${parentKey}.` : '') + key;
      indexedArray.push(indexedKey);
      if (subRows && expandedRows[key]) {
        generateRows(subRows, key);
      }
    });
  };
  generateRows(data);
  return indexedArray;
};

export const sortWithoutSubRows = (columnKey: string) => (rowA: Row, rowB: Row) => {
  if (rowA.original.subRows || rowB.original.subRows) {
    return 0;
  }
  return rowA.values[columnKey] > rowB.values[columnKey] ? 1 : -1;
};

interface GetConditionalSelectHeaderCheckboxProps {
  /** react-table's header props */
  headerProps: React.PropsWithChildren<HeaderProps<any>>;
  /** A predicate - based on your business logic - to determine whether a given row should be selectable */
  checkIfRowIsSelectable: (row: Row<any>) => boolean;
  /** Whether to allow page selection. Default: true */
  shouldSelectPage?: boolean;
}

/**
 * A convenience method for react-table headers for allowing conditional select
 * @param headerProps react-table's header props
 * @param checkIfRowIsSelectable A predicate - based on your business logic - to determine whether a given row should be selectable
 * @param shouldSelectPage Whether to allow page selection. Default: true
 * @returns Modified `checkboxProps` to enforce the conditional select
 */
export const getConditionalSelectHeaderCheckboxProps = ({
  headerProps,
  checkIfRowIsSelectable,
  shouldSelectPage = true
}: GetConditionalSelectHeaderCheckboxProps) => {
  // Note that in my comments I differentiate between the standard logic and the logic for the conditional select
  const checkIfAllSelectableRowsSelected = (rows: Row<any>[]) => rows.filter(checkIfRowIsSelectable).every((row) => row.isSelected);
  // Standard: Here we define the selection type for the next click: Select Page / Select All
  const isSelectPage =
    shouldSelectPage &&
    headerProps.page
      // For conditional select: Filter the rows based on your business logic
      .filter(checkIfRowIsSelectable)
      // Standard: `isSelectPage === true` if some of the rows are not yet selected
      // This (standard) logic might be confusing to understand at first, but - as a side note - the idea is as follows:
      // This is the variable that defines whether the header props that will be received FOR THE NEXT CLICK will be for Select Page or for Select All
      // Try to play this out in your head:
      //  - Initially, none of the rows are selected, so when we clicking the button initially, we will select only the (selectable) rows on the page (i.e. Select Page), hence the next click will be for Select All, hence `isSelectPage` will be `false`
      //  - When clicking again, we will select the rest of the (selectable) rows (i.e. Select All). The next click will again be Select All (for de-selecting all), hence `isSelectPage` will be `false`
      //  - Finally, when clicking again, we will de-select all rows. The next click will be for Select Page, hence `isSelectPage` will `true`
      .some((row) => !row.isSelected);

  // Standard: Get the props based on Select Page / Select All
  const checkboxProps = isSelectPage ? headerProps.getToggleAllPageRowsSelectedProps() : headerProps.getToggleAllRowsSelectedProps();

  // For conditional select: The header checkbox should be:
  //   - checked if all selectable rows are selected
  //   - indeterminate if only some selectable rows are selected (but not all)
  const disabled = headerProps.rows.filter(checkIfRowIsSelectable).length === 0;
  const checked = !disabled && checkIfAllSelectableRowsSelected(headerProps.rows);
  const indeterminate = !checked && headerProps.rows.some((row) => row.isSelected);

  // For conditional select: This is where the magic happens
  const onChange = () => {
    // If we're in Select All and all selectable rows are already selected: deselect all rows
    if (!isSelectPage && checkIfAllSelectableRowsSelected(headerProps.rows)) {
      headerProps.rows.forEach((row) => {
        headerProps.toggleRowSelected(row.id, false);
      });
    } else {
      // Otherwise:
      // First, define the rows to work with: if we're in Select Page, use `headerProps.page`, otherwise (Select All) use headerProps.rows
      const rows = isSelectPage ? headerProps.page : headerProps.rows;
      // Then select every selectable row
      rows.forEach((row) => {
        const checked = checkIfRowIsSelectable(row);
        headerProps.toggleRowSelected(row.id, checked);
      });
    }
  };

  // For conditional select: override checked, indeterminate and onChange - to enforce conditional select based on our business logic
  return {
    ...checkboxProps,
    checked,
    indeterminate,
    onChange,
    disabled
  };
};
